import { io, Socket } from 'socket.io-client'

let socket: Socket | null = null
let lastErrorTime = 0
let isConnecting = false
const ERROR_THROTTLE_MS = 10000 // Solo mostrar error cada 10 segundos

/**
 * Obtener la URL del servidor WebSocket
 * En producción, normaliza la URL para usar https:// si no está especificado
 */
const getWebSocketURL = (): string => {
  const isProduction = import.meta.env.PROD || window.location.protocol === 'https:'
  const envUrl = import.meta.env.VITE_WS_URL
  
  // Si no hay URL configurada, usar localhost en desarrollo
  if (!envUrl) {
    return isProduction ? '' : 'http://localhost:3002'
  }
  
  // Si estamos en producción y la URL usa http://, cambiarla a https://
  if (isProduction && envUrl.startsWith('http://') && !envUrl.includes('localhost')) {
    return envUrl.replace('http://', 'https://')
  }
  
  return envUrl
}

const WS_URL = getWebSocketURL()

/**
 * Sistema de logging condicional
 * Por defecto NO muestra logs. Solo muestra cuando VITE_DEBUG_WEBSOCKET=true explícitamente
 * Verificamos estrictamente solo el string 'true' para evitar falsos positivos
 * NOTA: Los logs están DESHABILITADOS por defecto para evitar spam en la consola
 */
const DEBUG_WEBSOCKET = false // Deshabilitado por defecto - cambiar a true solo para debugging
// const DEBUG_WEBSOCKET = String(import.meta.env.VITE_DEBUG_WEBSOCKET || '').toLowerCase() === 'true'
const IS_DEVELOPMENT = import.meta.env.DEV

// Por defecto, no mostrar logs a menos que esté explícitamente habilitado
const debugLog = (...args: any[]) => {
  // Solo mostrar si está explícitamente habilitado
  if (DEBUG_WEBSOCKET) {
    console.log('[WebSocket]', ...args)
  }
}

const debugWarn = (...args: any[]) => {
  // Solo mostrar si está explícitamente habilitado
  if (DEBUG_WEBSOCKET) {
    console.warn('[WebSocket]', ...args)
  }
}

const debugError = (...args: any[]) => {
  // Los errores solo se muestran si está explícitamente habilitado el debug
  // O en desarrollo pero solo la primera vez (para evitar spam)
  if (DEBUG_WEBSOCKET) {
    console.error('[WebSocket]', ...args)
  }
}

/**
 * Obtener token del localStorage
 */
const getToken = (): string | null => {
  return localStorage.getItem('token')
}

/**
 * Conectar al servidor WebSocket
 * Crea una nueva instancia de Socket.IO y se conecta automáticamente
 * @returns Instancia del socket o null si no hay token disponible
 */
export const connectWebSocket = (): Socket | null => {
  // Si ya existe un socket conectado, retornarlo
  if (socket?.connected) {
    return socket
  }

  // Si ya estamos intentando conectar, retornar el socket existente
  if (isConnecting && socket) {
    return socket
  }

  // Si existe un socket pero no está conectado, intentar reconectar
  if (socket && !socket.connected) {
    isConnecting = true
    socket.connect()
    // Resetear flag después de un tiempo
    setTimeout(() => { isConnecting = false }, 5000)
    return socket
  }

  const token = getToken()
  if (!token) {
    debugWarn('No hay token disponible para conectar WebSocket')
    return null
  }

  // Validar que la URL esté configurada
  if (!WS_URL) {
    debugError('❌ VITE_WS_URL no está configurada en producción. Por favor, configura la variable de entorno VITE_WS_URL en Vercel.')
    return null
  }

  isConnecting = true

  // Crear nueva instancia de Socket.IO
  // Por defecto, Socket.IO se conecta automáticamente (autoConnect: true)
  socket = io(WS_URL, {
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: 5,
    autoConnect: true // Asegurar conexión automática
  })

  socket.on('connect', () => {
    isConnecting = false
    debugLog('✅ WebSocket conectado:', socket?.id)
  })

  socket.on('disconnect', (reason) => {
    isConnecting = false
    // Solo loguear desconexiones si no es una desconexión normal
    if (reason !== 'io client disconnect') {
      debugLog('❌ WebSocket desconectado:', reason)
    }
  })

  socket.on('connect_error', (error) => {
    // Throttle: solo mostrar error cada 10 segundos para evitar spam
    const now = Date.now()
    if (now - lastErrorTime > ERROR_THROTTLE_MS) {
      lastErrorTime = now
      // En producción, siempre mostrar el error una vez para debugging
      const isProduction = import.meta.env.PROD || window.location.protocol === 'https:'
      if (DEBUG_WEBSOCKET || isProduction) {
        console.error('❌ Error de conexión WebSocket:', {
          message: error.message,
          url: WS_URL,
          hint: isProduction && !WS_URL.startsWith('https://') 
            ? 'Asegúrate de que VITE_WS_URL use https:// en producción' 
            : 'Verifica que el servidor WebSocket esté disponible y que CORS esté configurado correctamente'
        })
      }
    }
  })

  socket.on('error', (error) => {
    // Throttle: solo mostrar error cada 10 segundos para evitar spam
    const now = Date.now()
    if (now - lastErrorTime > ERROR_THROTTLE_MS) {
      lastErrorTime = now
      // Solo mostrar error si está explícitamente habilitado el debug
      if (DEBUG_WEBSOCKET) {
        debugError('❌ Error en WebSocket:', error)
      }
    }
  })

  return socket
}

/**
 * Desconectar del servidor WebSocket
 */
export const disconnectWebSocket = () => {
  if (socket) {
    socket.disconnect()
    socket = null
  }
}

/**
 * Obtener la instancia del socket (conectar si no está conectado)
 * Si el socket no existe o no está conectado, intenta crear/conectar uno nuevo
 * @returns Instancia del socket o null si no se puede conectar
 */
export const getSocket = (): Socket | null => {
  // Si no hay socket o no está conectado, intentar conectar
  if (!socket || !socket.connected) {
    return connectWebSocket()
  }
  return socket
}

// Cache para throttling de logs de estado
let lastStatusLogTime = 0
const STATUS_LOG_THROTTLE_MS = 5000 // Solo loguear estado cada 5 segundos

/**
 * Verificar si el socket está conectado
 * Verifica tanto la existencia del socket como su estado de conexión
 * @returns true si el socket existe y está conectado, false en caso contrario
 */
export const isSocketConnected = (): boolean => {
  // Verificar que el socket exista y esté conectado
  if (!socket) {
    // Solo loguear si está habilitado y no se ha logueado recientemente
    const now = Date.now()
    if (DEBUG_WEBSOCKET && (now - lastStatusLogTime > STATUS_LOG_THROTTLE_MS)) {
      lastStatusLogTime = now
      debugLog('No hay instancia de socket')
    }
    return false
  }
  // socket.connected es una propiedad booleana que Socket.IO actualiza automáticamente
  const connected = socket.connected === true
  // Solo loguear si está habilitado y no se ha logueado recientemente
  const now = Date.now()
  if (DEBUG_WEBSOCKET && (now - lastStatusLogTime > STATUS_LOG_THROTTLE_MS)) {
    lastStatusLogTime = now
    debugLog(`Estado de conexión: ${connected}, Socket ID: ${socket.id || 'N/A'}`)
  }
  return connected
}

/**
 * Obtener el ID del socket si está conectado
 * @returns ID del socket o null si no está conectado
 */
export const getSocketId = (): string | null => {
  if (socket?.connected && socket.id) {
    return socket.id
  }
  return null
}

